import React from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import get from 'lodash/get';
import find from 'lodash/find';

import {
  closeMenuItemAndGoToMenu,
  handleFormItemUpdate
} from 'bento-ordering/menus/actions/menuItem';
import { closeEditCartForm } from 'bento-ordering/cart/actions/editCart';
import { getTransformedRequiredFieldsForForm } from 'helpers/forms/menuItem';

import { UPDATE_ITEM } from 'constants/menu/ctaName';

import MenuItemDetailForm from 'bento-ordering/menus/containers/MenuItemDetailForm';
import LaunchNotificationOnMount from 'bento-ordering/base/containers/LaunchNotificationOnMount';
import ModalCloseButton from 'bento-ordering/base/ui_components/ModalCloseButton';
import { getMenuItemImage } from 'bento-ordering/menus/reducers/menuItemsDetail';
import { getItemByIdSelector } from 'bento-ordering/base/reducers/bentoApi';
import withLoading from 'bento-ordering/base/hoc/withLoading';
import { MENU_ITEMS_DETAIL } from 'bento-ordering/menus/constants/entities';
import { productPreviewHelperSelectorCartItem } from 'bento-ordering/menus/reducers/productPreview';
import { CART_ITEM } from 'constants/menu/formTypes';
import { getCartItemByIdSelector } from 'bento-ordering/cart/selectors';
import useGetModifierGroups from 'hooks/useGetModifierGroups';
import { PRODUCT_VARIANT_FIELD_NAME } from 'constants/menu/menuItemDetailForm';
import { useWouldExceedSpendingLimit } from 'bento-ordering/group_ordering/hooks/index';

const EditCartItem = ({
  menuItemImage,
  cartItemPreviewHelper,
  orderMaximum,
  menuItem,
  cart,
  match,
  location
}) => {
  const dispatch = useDispatch();
  const modifierGroups = useGetModifierGroups(menuItem);
  const { cartItemId } = match.params;
  const {
    hasExceededSpendingLimit,
    spendingLimit
  } = useWouldExceedSpendingLimit(cartItemId);

  const getPreSelectedFields = () => {
    const cartItemObject = getCartItemObject();

    if (!cartItemObject) {
      return {};
    }

    const {
      required_fields,
      quantity,
      order_made_for,
      special_instructions,
      variant
    } = cartItemObject;

    const requiredFields = getTransformedRequiredFieldsForForm(required_fields);
    const { id: productVariantId } = variant;

    return {
      ...requiredFields,
      quantityInput: quantity,
      'order-made-for': order_made_for,
      specialInstructions: special_instructions,
      [PRODUCT_VARIANT_FIELD_NAME]: productVariantId
    };
  };

  const getProductSlug = () => {
    const cartItemObject = getCartItemObject();
    return get(cartItemObject, 'item_slug', null);
  };

  const getCartItemObject = () => {
    const { items } = cart;
    const cartItemObject = find(items, { id: parseInt(cartItemId) });
    return cartItemObject;
  };

  const closeModal = () => {
    const path = get(location, 'pathname');
    dispatch(closeEditCartForm(path));
  };

  const {
    description,
    name,
    spicy,
    vegetarian,
    vegan,
    kosher,
    halal,
    organic,
    dairy,
    peanut,
    tree_nut,
    dairy_free,
    peanut_free,
    tree_nut_free,
    egg,
    shellfish,
    sesame,
    sesame_free,
    wheat,
    egg_free,
    shellfish_free,
    gluten_free,
    fish,
    soy,
    fish_free,
    soy_free,
    id,
    slug,
    variants
  } = menuItem;

  const menuItemDescriptionProps = {
    description,
    name,
    spicy,
    vegetarian,
    vegan,
    kosher,
    halal,
    organic,
    dairy,
    peanut,
    tree_nut,
    dairy_free,
    peanut_free,
    tree_nut_free,
    egg,
    shellfish,
    sesame,
    sesame_free,
    wheat,
    egg_free,
    shellfish_free,
    gluten_free,
    fish,
    soy,
    fish_free,
    soy_free
  };

  if (!getCartItemObject()) {
    // If trying to access cartItem from other session or ended session
    // Redirect to Menu or OO homepage
    dispatch(closeMenuItemAndGoToMenu());
  }

  return (
    <React.Fragment>
      {getProductSlug() && (
        <LaunchNotificationOnMount handleCallback={closeModal} />
      )}
      <ModalCloseButton
        autoFocus
        handleClick={closeModal}
        isWhite={!!menuItemImage}
      />
      <MenuItemDetailForm
        defaultCtaName={UPDATE_ITEM}
        preSelectedFields={getPreSelectedFields()}
        handleFormItemSubmit={handleFormItemUpdate}
        cartItemId={cartItemId}
        productId={id}
        productName={name}
        productSlug={slug}
        orderMaximum={orderMaximum}
        formType={CART_ITEM}
        menuItemImage={menuItemImage}
        menuItemDescriptionProps={menuItemDescriptionProps}
        modifierGroups={modifierGroups}
        variants={variants}
        {...cartItemPreviewHelper}
        spendingLimit={spendingLimit}
        hasExceededSpendingLimit={hasExceededSpendingLimit}
      />
    </React.Fragment>
  );
};

EditCartItem.propTypes = {
  location: PropTypes.object,
  cart: PropTypes.shape({
    online_ordering_location: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    items: PropTypes.array
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
      menuItemSlug: PropTypes.string,
      cartItemId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  }),
  menuItem: PropTypes.object,
  menuItemImage: PropTypes.object,
  cartItemPreviewHelper: PropTypes.object,
  orderMaximum: PropTypes.string
};

const mapStateToProps = (state, ownProps) => {
  const { cartItemId } = ownProps.match.params;

  const cartItem =
    getCartItemByIdSelector(state.cart, parseInt(cartItemId)) || {};
  const menuItem = getItemByIdSelector(state.menuItemsDetail, cartItem.product);
  const orderMaximum = get(state.cart, 'max_order_amount', 0);

  return {
    menuItem,
    cartItemPreviewHelper: productPreviewHelperSelectorCartItem(state),
    menuItemImage: getMenuItemImage(menuItem),
    cart: state.cart,
    form: state.form,
    orderMaximum
  };
};

export default withRouter(
  connect(mapStateToProps)(withLoading(EditCartItem, MENU_ITEMS_DETAIL))
);
